import { Intro, DataTable, Pill, PopOverMenu } from 'components/commons';
import { DriverStatus } from 'enums';
import { useApi, useFilter, useMount } from 'hooks';
import useModal from 'hooks/useModal';
import React, { useCallback, useMemo } from 'react';
import { usersColumns } from './users-columns';
import { usersFilterState } from './users-filter.state';
import styles from './users.module.scss';
import locale from 'localization';
import UsersStatusModal from './users-status-modal';
import { prettifyUserRole, prettifyVehicleStatus } from 'utils/pretty.utils';
import VehicleFilter from './users-filter';
import { getUsers } from 'apis/user.api';
import UserRole from 'enums/user-role';
import UsersForm from './users-form';

const UsersModule = () => {
  const statusModal = useModal();

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(usersFilterState());
  const userFormModal = useModal();

  const {
    request: getUsersRequest,
    loading: fetchingUsers,
    result: getUsersResult = { users: [], count: 0 }
  } = useApi({
    api: getUsers,
    pageError: true
  });

  useMount(() => {
    fetchUsers(requestState);
  });

  const fetchUsers = useCallback(
    (rs) => {
      getUsersRequest(submitFilter({ ...rs }));
    },
    [getUsersRequest, submitFilter]
  );

  const changeStatusCb = useCallback(
    (userId, firstName, lastName, status) => {
      statusModal.show({ firstName, lastName, status, userId });
    },
    [statusModal]
  );

  const preparedUsersData = useMemo(() => {
    const { users } = getUsersResult;
    if (users.length > 0) {
      const preparedData = users.map((d) => {
        const map = new Map();
        map.set('firstName', d.firstName);
        map.set('lastName', d.lastName);
        map.set('username', d.username);
        map.set(
          'role',
          <Pill
            cement={d.role === UserRole.Admin}
            cheddar={d.role === UserRole.Inspector}
            grass={d.role === UserRole.Dispatcher}
            sky={d.role === UserRole.Staff}
          >
            {prettifyUserRole(d.role)}
          </Pill>
        );
        map.set(
          'status',
          <Pill grass={d.status === DriverStatus.Active} cheddar={d.status === DriverStatus.Inactive}>
            {prettifyVehicleStatus(d.status)}
          </Pill>
        );
        let options = [];
        let editDetails;
        let deactivateUser;

        editDetails = {
          content: locale.editDetails,
          onClick: () => {
            userFormModal.show({
              title: locale.editDetails,
              actionText: locale.save,
              create: false,
              userId: d.userId,
              initialState: {
                firstName: {
                  value: d.firstName
                },
                lastName: {
                  value: d.lastName
                },
                username: {
                  value: d.username
                },
                role: {
                  value: d.role
                },
                password: {
                  value: d.passwordRaw
                }
              }
            });
          }
          // disabled: deactivated
        };
        options.push(editDetails);

        deactivateUser = {
          content: d.status === DriverStatus.Active ? locale.deactivate : locale.activate,
          onClick: () => {
            changeStatusCb(d.userId, d.firstName, d.lastName, d.status);
          }
          // disabled: deactivated
        };
        options.push(deactivateUser);
        map.set('options', <PopOverMenu options={options} />);
        return map;
      });
      return preparedData;
    }
    return [];
  }, [changeStatusCb, getUsersResult, userFormModal]);

  const onStatusChangeCb = useCallback(
    (status) => {
      const { requestState } = modifyFilters({ status, page: 1 });
      fetchUsers(requestState);
    },
    [fetchUsers, modifyFilters]
  );

  const onRoleChangeCb = useCallback(
    (role) => {
      const { requestState } = modifyFilters({ role, page: 1 });
      fetchUsers(requestState);
    },
    [fetchUsers, modifyFilters]
  );

  const onSearchCb = useCallback(
    (searchKey) => {
      const { requestState } = modifyFilters({ searchKey, page: 1 });
      fetchUsers(requestState);
    },
    [fetchUsers, modifyFilters]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchUsers(requestState);
    },
    [fetchUsers, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchUsers(requestState);
    },
    [fetchUsers, modifyFilters]
  );

  return (
    <div>
      <UsersForm
        {...userFormModal}
        refreshPage={() => {
          getUsersRequest(requestState);
        }}
      />
      <div>
        <Intro
          title={'Manage users'}
          subtitle={'Manage your users and keep their information up to date.'}
          actionText={locale.addUser}
          actionOnClick={() => {
            userFormModal.show({
              title: locale.addUser,
              actionText: locale.add,
              create: true,
              initialState: {
                firstName: {
                  value: ''
                },
                lastName: {
                  value: ''
                },
                username: {
                  value: ''
                },
                password: {
                  value: ''
                }
              }
            });
          }}
          // actionDisabled={}
        />
      </div>
      <div className={styles.filters}>
        <div>
          <VehicleFilter
            filterState={filterState}
            onStatusChange={onStatusChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
            onRoleChange={onRoleChangeCb}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingUsers}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={usersColumns}
          data={preparedUsersData}
          dataCount={getUsersResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
        />
      </div>

      <UsersStatusModal refetchUsers={() => fetchUsers(requestState)} {...statusModal} />
    </div>
  );
};

export default UsersModule;
